import type { StatusTone } from '@/components/ui/status-badge';

import type {
  ContactRoleDto,
  ContactStateDto,
  EnrichmentDegradedReasonDto,
  EnrichmentProvenanceDto,
  EstablishmentStateDto,
  TokenActivationStatusDto,
} from '@/api/supplier-client';

// =============================================================================
// supplier-labels.ts — Mapping enums ASCII (API) → libellés FR + variant badge.
// =============================================================================
// L'API porte des enums ASCII (parité Prisma) ; l'UI les rend en libellés FR
// accentués (i18n front, decisions.md STOP-1). WCAG 2.1 AA (ST-NFR-03) : la
// COULEUR n'est JAMAIS le seul porteur d'information — chaque état rend un
// LIBELLÉ TEXTE ; le variant de badge n'est qu'un renfort visuel.
//
// Aucune règle métier ici : table de traduction pure (état → libellé/variant).

/** Variant de badge shadcn générique (cf. components/ui/badge.tsx) — réutilisé
 *  hors statut de compte (ex. rate-collection-labels). */
export type BadgeVariant = 'default' | 'secondary' | 'outline' | 'accent';

/** Couple libellé FR + tone DS pour un statut de compte (StatusBadge, DS Cat 1). */
interface LabelWithTone {
  readonly label: string;
  readonly tone: StatusTone;
}

/** 6 états établissement → libellés FR + tone DS (mapping canonique Cat 1, decisions.md STOP-1). */
const ESTABLISHMENT_STATE_LABELS: Record<EstablishmentStateDto, LabelWithTone> = {
  Brouillon: { label: 'Brouillon', tone: 'inactive' },
  Soumis: { label: 'Soumis', tone: 'invited' },
  Refuse: { label: 'Refusé', tone: 'refused' },
  Actif: { label: 'Actif', tone: 'active' },
  Suspendu: { label: 'Suspendu', tone: 'suspended' },
  Archive: { label: 'Archivé', tone: 'inactive' },
};

/** Libellé FR + tone DS d'un état d'établissement. */
export function establishmentStateLabel(
  state: EstablishmentStateDto,
): LabelWithTone {
  return ESTABLISHMENT_STATE_LABELS[state];
}

/**
 * Statut d'activation DÉRIVÉ du lien → libellé FR du CHIP de filtre (T2-REGISTRE). Le TEXTE porte
 * l'information du filtre (WCAG 2.1 AA : jamais une couleur seule). « En attente » = lien Envoyé
 * encore valide (parité vocabulaire GATE_HUMAN « Activé / En attente / Expiré »).
 */
const TOKEN_ACTIVATION_FILTER_LABELS: Record<TokenActivationStatusDto, string> = {
  Active: 'Activé',
  Envoye: 'En attente',
  Expire: 'Expiré',
};

/** Libellé FR du chip « Statut d'activation » (filtre registre). */
export function tokenActivationStatusFilterLabel(
  status: TokenActivationStatusDto,
): string {
  return TOKEN_ACTIVATION_FILTER_LABELS[status];
}

/** Rôle d'un contact → libellé FR. */
const CONTACT_ROLE_LABELS: Record<ContactRoleDto, string> = {
  MASTER: 'Maître',
  COLLABORATEUR: 'Collaborateur',
};

/** Libellé FR d'un rôle de contact. */
export function contactRoleLabel(role: ContactRoleDto): string {
  return CONTACT_ROLE_LABELS[role];
}

/** État d'un contact → libellé FR + tone DS (mapping canonique Cat 1). */
const CONTACT_STATE_LABELS: Record<ContactStateDto, LabelWithTone> = {
  Invite: { label: 'Invité', tone: 'invited' },
  Actif: { label: 'Actif', tone: 'active' },
  Inactif: { label: 'Inactif', tone: 'inactive' },
  ResignatureRequise: { label: 'Re-signature requise', tone: 'suspended' },
};

/** Libellé FR + tone DS d'un état de contact. */
export function contactStateLabel(state: ContactStateDto): LabelWithTone {
  return CONTACT_STATE_LABELS[state];
}

/** Provenance d'un snapshot d'enrichissement → libellé FR. */
const PROVENANCE_LABELS: Record<EnrichmentProvenanceDto, string> = {
  enrichi: 'Enrichi (DataGouv)',
  saisie_manuelle: 'Saisie manuelle',
  perime: 'Périmé (dernier connu)',
};

/** Libellé FR d'une provenance d'enrichissement. */
export function provenanceLabel(provenance: EnrichmentProvenanceDto): string {
  return PROVENANCE_LABELS[provenance];
}

/**
 * Raison du mode dégradé fail-open → libellé FR (miroir de l'enum ACL EnrichmentDegradedReason).
 * Doublage TEXTE de l'information (WCAG 2.1 AA) : jamais portée par la seule couleur.
 */
const ENRICHMENT_DEGRADED_REASON_LABELS: Record<EnrichmentDegradedReasonDto, string> = {
  timeout: "Délai d'attente dépassé",
  unreachable: 'Source injoignable',
  http_error: 'Erreur de la source',
  not_found: 'SIRET inconnu de la source',
};

/** Libellé FR d'une raison de mode dégradé. */
export function enrichmentDegradedReasonLabel(
  reason: EnrichmentDegradedReasonDto,
): string {
  return ENRICHMENT_DEGRADED_REASON_LABELS[reason];
}
